import { useParams, Navigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingContactWidget from "@/components/FloatingContactWidget";
import { RelatedTerms } from "@/components/glossar/RelatedTerms";
import { glossarTerms } from "@/utils/glossarData";
import { getGlossaryTermSchema } from "@/utils/seoSchemas";
import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { 
  BookOpen, 
  Shield, 
  AlertTriangle, 
  CheckCircle2, 
  ExternalLink,
  FileText,
  ArrowLeft
} from "lucide-react";

const categoryLabels = {
  bedrohungen: "Cyber-Bedrohungen",
  compliance: "Compliance & Recht",
  sicherheit: "Technische Sicherheit",
  medizin: "Medizin-IT",
  incident: "Incident Response"
};

const categoryColors = {
  bedrohungen: "bg-red-500/10 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800",
  compliance: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800",
  sicherheit: "bg-green-500/10 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800",
  medizin: "bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800",
  incident: "bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800"
};

const GlossarBegriff = () => {
  const { slug } = useParams<{ slug: string }>();
  const term = glossarTerms.find(t => t.slug === slug);

  if (!term) {
    return <Navigate to="/glossar" replace />;
  }

  const breadcrumbItems = [
    { label: "Glossar", url: "/glossar" },
    { label: term.name }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{term.name} einfach erklärt | Cyber-Glossar Arztpraxen</title>
        <meta name="description" content={term.shortDef} />
        <meta 
          name="keywords" 
          content={`${term.name} Definition, Was ist ${term.name}, ${term.name} einfach erklärt, ${term.name} Arztpraxis`} 
        />
        <link rel="canonical" href={`https://praxis-versichert.de/glossar/${term.slug}`} />
        <script type="application/ld+json">
          {JSON.stringify(getGlossaryTermSchema({ ...term, slug: term.slug }))}
        </script>
      </Helmet>

      <Navigation />
      
      <main className="flex-grow">
        <div className="container py-8">
          <Button variant="ghost" size="sm" asChild className="mb-6">
            <Link to="/glossar">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zurück zum Glossar
            </Link>
          </Button>

          <PageBreadcrumbs items={breadcrumbItems} />

          <article className="max-w-4xl mx-auto mt-8">
            {/* Header */}
            <header className="mb-8">
              <Badge 
                variant="outline" 
                className={`mb-4 ${categoryColors[term.category as keyof typeof categoryColors]}`}
              >
                {categoryLabels[term.category as keyof typeof categoryLabels]}
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">{term.name}</h1>
              <p className="text-xl text-muted-foreground leading-relaxed border-l-4 border-primary pl-6 py-2">
                {term.shortDef}
              </p>
            </header>

            <Separator className="my-8" />

            {/* Long Description */}
            <section className="prose prose-lg dark:prose-invert max-w-none mb-8">
              <h2 className="flex items-center gap-2 text-2xl font-semibold">
                <BookOpen className="h-6 w-6 text-primary" />
                Was ist {term.name}?
              </h2>
              <p className="text-foreground/90 leading-relaxed">{term.longDescription}</p>
            </section>

            {/* How It Works */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  Wie funktioniert {term.name}?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">{term.howItWorks}</p>
              </CardContent>
            </Card>

            {/* Medical Example */}
            <Card className="mb-8 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-primary" />
                  Praxis-Beispiel
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/90 leading-relaxed italic">
                  {term.exampleMedical}
                </p>
              </CardContent>
            </Card>

            {/* Prevention Measures */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  Präventionsmaßnahmen
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {term.prevention.map((measure, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-foreground/90">{measure}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Insurance Coverage */}
            <Card className="mb-8 bg-accent/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  Cyberversicherungs-Schutz
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/90 leading-relaxed mb-4">
                  {term.insuranceCoverage}
                </p>
                <Button asChild>
                  <Link to="/cyberversicherung/vergleichsrechner">
                    Versicherungsschutz prüfen
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Resources */}
            {term.resources.length > 0 && (
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    Weiterführende Ressourcen
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {term.resources.map((resource, idx) => (
                      <Link
                        key={idx}
                        to={resource.url}
                        className="flex items-center gap-2 text-primary hover:underline"
                      >
                        <ExternalLink className="h-4 w-4" />
                        {resource.title}
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Related Terms */}
            <RelatedTerms relatedSlugs={term.relatedTerms} />
          </article>

          {/* CTA Section */}
          <section className="max-w-4xl mx-auto mt-16 p-8 rounded-lg bg-gradient-to-br from-primary/10 via-background to-accent/10 border border-border text-center">
            <h2 className="text-2xl font-bold mb-4">
              Individuelle Beratung zu {term.name}
            </h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Unsere Cyber-Experten beraten Sie gerne persönlich zu diesem Thema 
              und finden die passende Absicherung für Ihre Praxis.
            </p>
            <Button size="lg" asChild>
              <Link to="/termin-buchen">
                Kostenlose Beratung vereinbaren
              </Link>
            </Button>
          </section>
        </div>
      </main>

      <Footer />
      <FloatingContactWidget />
    </div>
  );
};

export default GlossarBegriff;
